"use client";

import { motion, MotionConfig } from "motion/react";
import { useLang } from "./lang-provider";
import { DecoLayer } from "./deco-layer";
import { Nav } from "./nav";
import { Footer } from "./footer";
import { Sprite } from "./sprite";

/* ─────────────────────────────────────────────────────────────
   Fade-in on scroll — lightweight wrapper using motion/react
   ───────────────────────────────────────────────────────────── */
function FadeIn({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, delay }}
    >
      {children}
    </motion.div>
  );
}

export function Home() {
  const { lang, t } = useLang();

  const appStoreUrl = "https://apps.apple.com/app/apple-store/id6762003033?pt=128149478&ct=PaulOfficialWebsite&mt=8";

  const badgeSrc = lang === "fr" ? "/app-store-badge-fr.png?v=2" : "/app-store-badge.png?v=2";
  // Official Apple badge aspect ratios: EN 119.66×40, FR 126.51×40 — keep height=44px via CSS

  /* Screenshot mapping (FR and EN share the same filenames) */
  const screenPath = (name: string) => `/screens/${lang}/${name}`;

  return (
    <MotionConfig reducedMotion="user">
      {/* Animated background glows */}
      <div className="bento-bg-layer" aria-hidden="true">
        <div className="bento-glow bento-glow-1" />
        <div className="bento-glow bento-glow-2" />
        <div className="bento-glow bento-glow-3" />
        <div className="bento-glow bento-glow-4" />
      </div>

      {/* Floating sprite deco layer (existing component — kept behind bento) */}
      <DecoLayer />

      <Nav />

      <main>
      {/* ══════════════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════════════ */}
      <section className="hero-section">
        <motion.div
          className="hero-text"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <span className="hero-eyebrow">{t.heroEyebrow}</span>
          <h1 className="hero-h1">
            {t.heroH1Part1}
            <br />
            <em>{t.heroH1Em}</em>
            <br />
            {t.heroH1Part2}
          </h1>
          <p className="hero-sub">{t.heroSub}</p>
          <div className="hero-actions">
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hero-badge-link"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={badgeSrc} alt={t.ctaBadgeAlt} className="appstore-badge" />
            </a>
            <span className="hero-note">{t.heroNote}</span>
          </div>
        </motion.div>
        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="hero-bear-wrap">
            <Sprite name="lift" scale={1.6} fps={8} startFrame={0} />
          </div>
        </motion.div>
      </section>

      {/* ══════════════════════════════════════════════════
          BENTO WALL
      ══════════════════════════════════════════════════ */}
      <div className="bento-page">
        <div className="bento">

          {/* ══ BAND 1 (rows 1–11) ══════════════════════════════ */}

          {/* PILLAR A — col 1–5, rows 1–4 */}
          <FadeIn className="brick glass b-pila" delay={0}>
            <div className="bi" style={{ justifyContent: "center", gap: 8 }}>
              <div>
                <span className="eyebrow">{t.pillarAEyebrow}</span>
                <h2 className="bh2">{t.pillarATitle}</h2>
                <p className="bp" style={{ maxWidth: 320 }}>{t.pillarADesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* FEATURE Saisie naturelle — col 6–8, rows 1–4 */}
          <FadeIn className="brick glass b-fsaisie" delay={0.05}>
            <div className="bi c">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#FF9F0A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              <div>
                <h3 className="bh3">{t.featSaisieTitle}</h3>
                <p className="bp sm">{t.featSaisieDesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* PHONE p1_home — col 9–12, rows 1–11 */}
          <FadeIn className="brick phone b-p1" delay={0.1}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={screenPath("p1_home.png")} alt={t.screen1Alt} width={1168} height={2264} />
          </FadeIn>

          {/* TAGLINE orange — col 1–3, rows 5–7 */}
          <FadeIn className="brick orange b-tag" delay={0.08}>
            <div className="bi c">
              <p className="tagline-lg" style={{ whiteSpace: "pre-line" }}>{t.tagline}</p>
            </div>
          </FadeIn>

          {/* BEAR 1 — col 4–5, rows 5–7 */}
          <div className="brick bear-bg b-bear1">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
              {/* Bbox measured: 187x116+34+22, fw=256, fh=144, scale=0.85 */}
              <div style={{ transform: "translate(0.4px, -6.8px)" }}>
                <Sprite name="lift" scale={0.85} fps={8} startFrame={0} />
              </div>
            </div>
          </div>

          {/* FEATURE Dictée vocale — col 6–8, rows 5–7 */}
          <FadeIn className="brick glass b-fvoice" delay={0.1}>
            <div className="bi c">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#FF9F0A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
              <div>
                <h3 className="bh3">{t.featVoiceTitle}</h3>
                <p className="bp sm">{t.featVoiceDesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* STEP 1 — col 1–4, rows 8–11 */}
          <FadeIn className="brick dark b-step1" delay={0.12}>
            <div className="bi" style={{ justifyContent: "center", alignItems: "center", gap: 16, flexDirection: "row", padding: "22px 24px" }}>
              <div className="stepnum w">1</div>
              <div>
                <h3 className="bh3 w" style={{ marginBottom: 4 }}>{t.step1Title}</h3>
                <p className="bp w sm">{t.step1Desc}</p>
              </div>
            </div>
          </FadeIn>

          {/* FEATURE Carte musculaire — col 5–6, rows 8–11 */}
          <FadeIn className="brick glass b-fmuscle" delay={0.14}>
            <div className="bi c">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FF9F0A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              <div>
                <h3 className="bh3" style={{ fontSize: 14 }}>{t.featMuscleTitle}</h3>
                <p className="bp sm" style={{ fontSize: 12 }}>{t.featMuscleDesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* FEATURE Explorateur — col 7–8, rows 8–11 */}
          <FadeIn className="brick orange b-fexpl" delay={0.16}>
            <div className="bi c">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
              </svg>
              <div>
                <h3 className="bh3 w" style={{ fontSize: 14 }}>{t.featExplTitle}</h3>
                <p className="bp w sm" style={{ fontSize: 12 }}>{t.featExplDesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* ══ BAND 2 (rows 12–19) ═══════════════════════════ */}

          {/* PHONE p2_voice — col 1–3, rows 12–19 */}
          <FadeIn className="brick phone b-p2" delay={0.05}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={screenPath("p2_voice.png")} alt={t.screen2Alt} width={1168} height={2264} />
          </FadeIn>

          {/* PILLAR B — col 4–7, rows 12–16 */}
          <FadeIn className="brick glass b-pilb" delay={0.08}>
            <div className="bi" style={{ justifyContent: "center", gap: 8 }}>
              <div>
                <span className="eyebrow">{t.pillarBEyebrow}</span>
                <h2 className="bh2">{t.pillarBTitle}</h2>
                <p className="bp" style={{ maxWidth: 280 }}>{t.pillarBDesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* STEP 2 — col 4–7, rows 17–19 */}
          <FadeIn className="brick dark b-step2" delay={0.1}>
            <div className="bi" style={{ justifyContent: "center", alignItems: "center", gap: 16, flexDirection: "row", padding: "22px 24px" }}>
              <div className="stepnum w">2</div>
              <div>
                <h3 className="bh3 w" style={{ marginBottom: 4 }}>{t.step2Title}</h3>
                <p className="bp w sm">{t.step2Desc}</p>
              </div>
            </div>
          </FadeIn>

          {/* PHONE p3_exercise — col 8–10, rows 12–19 */}
          <FadeIn className="brick phone b-p3" delay={0.1}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={screenPath("p3_exercise.png")} alt={t.screen3Alt} width={1168} height={2264} />
          </FadeIn>

          {/* BEAR 2 — col 11–12, rows 12–15 */}
          <div className="brick bear-bg b-bear2">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
              {/* Bbox measured: 113x101+35+43, fw=162, fh=144, scale=0.90 */}
              <div style={{ transform: "translate(-9.5px, -19.4px)" }}>
                <Sprite name="trophy" fw={162} scale={0.90} fps={7} startFrame={2} />
              </div>
            </div>
          </div>

          {/* FEATURE Records — col 11–12, rows 16–19 */}
          <FadeIn className="brick glass b-frecords" delay={0.12}>
            <div className="bi c">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FF9F0A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                <path d="M4 22h16" />
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
              </svg>
              <div>
                <h3 className="bh3" style={{ fontSize: 13 }}>{t.featRecordsTitle}</h3>
                <p className="bp sm" style={{ fontSize: 12 }}>{t.featRecordsDesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* ══ BAND 3 (rows 20–30) ═══════════════════════════ */}

          {/* STEP 3 — col 1–4, rows 20–23 */}
          <FadeIn className="brick dark b-step3" delay={0.06}>
            <div className="bi" style={{ justifyContent: "center", alignItems: "center", gap: 16, flexDirection: "row", padding: "22px 24px" }}>
              <div className="stepnum w">3</div>
              <div>
                <h3 className="bh3 w" style={{ marginBottom: 4 }}>{t.step3Title}</h3>
                <p className="bp w sm">{t.step3Desc}</p>
              </div>
            </div>
          </FadeIn>

          {/* FEATURE Historique — col 1–4, rows 24–27 */}
          <FadeIn className="brick glass b-fhist" delay={0.08}>
            <div className="bi" style={{ justifyContent: "center", alignItems: "center", gap: 16, flexDirection: "row" }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#FF9F0A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 .49-3" />
              </svg>
              <div>
                <h3 className="bh3" style={{ marginBottom: 4 }}>{t.featHistTitle}</h3>
                <p className="bp sm">{t.featHistDesc}</p>
              </div>
            </div>
          </FadeIn>

          {/* BEAR 3 — col 1–4, rows 28–30 */}
          <div className="brick bear-bg b-bear3">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
              {/* Bbox measured: 100x120+78+18, fw=256, fh=144, scale=0.85 */}
              <div style={{ transform: "translate(0px, -5.1px)" }}>
                <Sprite name="idle" scale={0.85} fps={6} startFrame={5} />
              </div>
            </div>
          </div>

          {/* PHONE p4_bodymap — col 5–8, rows 20–30 */}
          <FadeIn className="brick phone b-p4" delay={0.05}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={screenPath("p4_bodymap.png")} alt={t.screen4Alt} width={1168} height={2264} />
          </FadeIn>

          {/* PHONE p5_explorer — col 9–12, rows 20–30 */}
          <FadeIn className="brick phone b-p5" delay={0.1}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={screenPath("p5_explorer.png")} alt={t.screen5Alt} width={1168} height={2264} />
          </FadeIn>

          {/* ══ BAND 4 (rows 31–38) ═══════════════════════════ */}

          {/* PHONE p6_achievements — col 1–3, rows 31–38 */}
          <FadeIn className="brick phone b-p6" delay={0.05}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={screenPath("p6_achievements.png")} alt={t.screen6Alt} width={1168} height={2264} />
          </FadeIn>

          {/* FEATURE Analyse de séance — col 4–6, rows 31–34 */}
          <FadeIn className="brick glass b-frec2" delay={0.08}>
            <div className="bi c">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF9F0A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
              <div>
                <h3 className="bh3">{t.featRec2Title}</h3>
                <p className="bp sm">{t.featRec2Desc}</p>
              </div>
            </div>
          </FadeIn>

          {/* FEATURE Hors-ligne — col 7–8, rows 31–34 */}
          <FadeIn className="brick glass b-fnat2" delay={0.1}>
            <div className="bi c">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FF9F0A" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
              <div>
                <h3 className="bh3" style={{ fontSize: 13 }}>{t.featNat2Title}</h3>
                <p className="bp sm" style={{ fontSize: 12 }}>{t.featNat2Desc}</p>
              </div>
            </div>
          </FadeIn>

          {/* BEAR 3B — col 4–8, rows 35–38 */}
          <div className="brick bear-bg b-bear3b">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", gap: 24 }}>
              {/* Bbox measured: 140x131+22+13, fw=162, fh=144, scale=0.85 */}
              <div style={{ transform: "translate(-9.3px, -5.5px)" }}>
                <Sprite name="trophy" fw={162} scale={0.85} fps={7} startFrame={4} />
              </div>
            </div>
          </div>

          {/* CTA BRICK — col 9–12, rows 31–38 */}
          <div className="brick orange b-cta" style={{ overflow: "hidden", position: "relative" }}>
            <div style={{ position: "absolute", right: -24, bottom: -16, opacity: 0.13, zIndex: 0 }}>
              <Sprite name="lift" scale={1.8} fps={7} startFrame={3} />
            </div>
            <div className="bi c" style={{ position: "relative", zIndex: 1, gap: 14 }}>
              <p className="tagline-lg" style={{ fontSize: 20 }}>{t.ctaTitle}</p>
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-badge-link"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={badgeSrc} alt={t.ctaBadgeAlt} className="appstore-badge" />
              </a>
              <span className="bnote w">{t.heroNote}</span>
            </div>
          </div>

        </div>{/* /bento */}
      </div>{/* /bento-page */}
      </main>

      <Footer />
    </MotionConfig>
  );
}
